import { useState, useEffect } from 'react';
import { signInWithPopup, signInWithEmailAndPassword, GoogleAuthProvider } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, isEmulatorMode } from '../lib/firebase';
import { defaultLandingForRole, useAuthStore } from '../store/useAuthStore';

/** Seeded emulator accounts — one per role, created by `scripts/seed-emulator.mjs`
 *  with this shared password. Emulator-only convenience: each button performs a
 *  REAL (emulated) password sign-in, so the whole auth pipeline — whitelist
 *  lookup, role resolution, `refreshRoleClaim` — runs exactly as in prod.
 *  This replaced the old "Local Offline Dev Bypass" localStorage trick when the
 *  offline dual path was retired (Phase 3 of the retirement plan). */
const EMULATOR_DEV_PASSWORD = 'wss-dev-password';
const EMULATOR_DEV_ACCOUNTS: Array<{ label: string; description: string; email: string }> = [
  { label: 'Admin', description: 'See everything', email: 'admin@wss.com.sg' },
  { label: 'Sales', description: 'Quote / order authorship', email: 'sales1@wss.com.sg' },
  {
    label: 'Sales Mgr',
    description: 'Pipeline oversight + cross-rep authority',
    email: 'salesmgr@wss.com.sg',
  },
  { label: 'Staff', description: 'Receipts + own claims', email: 'staff1@wss.com.sg' },
  { label: 'Measurer', description: 'Schedule + assigned MCs only', email: 'measurer@wss.com.sg' },
  { label: 'Finance', description: 'Payments + read-only ops', email: 'finance@wss.com.sg' },
  {
    label: 'Finance Mgr',
    description: 'Approves claims, pushes payouts',
    email: 'finmgr@wss.com.sg',
  },
  { label: 'Purchaser', description: 'Procurement: PRs + vendors', email: 'purchaser@wss.com.sg' },
];

export default function LoginPortal() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, authInitialized } = useAuthStore();
  const [authError, setAuthError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  // Redirect target after sign-in. If the user was bounced here from a
  // protected route, return to that route. Otherwise route by role —
  // post-ADR-0005-Revision-1, STAFF lands on /staff/receipts (not /staff/home)
  // and the other path-fenced roles each have their own primary surface. The
  // earlier hardcoded `/staff/home` fallback meant only ADMIN / SALES landed
  // on their actual default; everyone else had to wait for ProtectedRoute's
  // path-fence to bounce them, and STAFF (who has /staff/home in their prefix
  // list as a safety-net) never bounced at all.
  const fromState = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;

  useEffect(() => {
    if (user && authInitialized) {
      const target = fromState ?? defaultLandingForRole(role);
      navigate(target, { replace: true });
    }
  }, [user, authInitialized, navigate, role, fromState]);

  const handleGoogleLogin = async () => {
    setAuthError('');
    setIsAuthenticating(true);
    try {
      const provider = new GoogleAuthProvider();
      // The redirect/kick logic is natively handled by the useAuthStore session listener
      // It will evaluate the whitelisting upon successful Google PopUp completion.
      await signInWithPopup(auth, provider);
    } catch (e) {
      const err = e as { code?: string; message?: string };
      if (err.code === 'auth/popup-closed-by-user') {
        setAuthError('Authentication aborted.');
      } else {
        setAuthError(err.message || 'Invalid Google credentials.');
      }
      setIsAuthenticating(false);
    }
  };

  // If we just popped up and the listener is doing the DB check, we might see useAuthStore boot the user back out.
  // We can hook into the authInitialized state to know when the background is idle.
  // But wait, if they aren't whitelisted, they drop back down to user = null
  // We can display an un-whitelisted generic error next time they hit the button.

  return (
    <div className="bg-background font-body min-h-screen flex flex-col">
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="bg-white max-w-md w-full p-6 sm:p-10 rounded-2xl border border-outline-variant shadow-lg relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1.5 bg-primary"></div>

          <div className="text-center mb-10 mt-2">
            <img
              src="/WSS%20Logo.png"
              alt="WSS Windows & Doors"
              className="h-14 w-auto object-contain mx-auto mb-5"
            />
            <h1 className="text-2xl font-bold font-headline text-secondary">
              Internal Staff Portal
            </h1>
            <p className="text-[11px] text-outline font-bold tracking-[0.2em] uppercase mt-3 bg-surface-container py-1.5 px-3 rounded inline-block">
              Strictly Invitation Only
            </p>
          </div>

          {authError && (
            <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg border border-red-100 flex items-start gap-2 mb-6">
              <span className="material-symbols-outlined text-sm mt-0.5">warning</span>
              <p className="font-medium leading-tight">{authError}</p>
            </div>
          )}

          <button
            onClick={() => void handleGoogleLogin()}
            disabled={isAuthenticating}
            className="w-full bg-white border border-outline-variant text-secondary py-4 rounded-xl font-bold text-lg hover:border-primary/50 hover:bg-surface-container-low active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-3 shadow-sm hover:shadow"
          >
            <img
              src="https://lh3.googleusercontent.com/COxitqgJr1sJnIDe8-jiKhxDx1FrYbtRHKJ9z_hELisAlapwE9LUPh6fcXIfb5vwpbMl4xl9H9TRFPc5NOO8Sb3VSgIBrfRYvW6cUA"
              alt="Google"
              className="w-5 h-5 grayscale opacity-80"
            />
            {isAuthenticating ? 'Authenticating with Google...' : 'Sign in with Google'}
          </button>

          {/* Emulator-only dev sign-in: one click per seeded role account,
              via a real (emulated) password sign-in — the same auth pipeline
              as prod, minus the popup round-trip. Stripped from production
              builds (import.meta.env.DEV) and hidden outside emulator mode. */}
          {import.meta.env.DEV && isEmulatorMode && (
            <div className="mt-4 border border-outline-variant border-dashed rounded-xl p-3 bg-outline-variant/10">
              <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-outline mb-2 px-1">
                <span className="material-symbols-outlined text-[14px]">developer_mode</span>
                Dev sign-in (emulator) — pick a role
              </div>
              <div className="grid grid-cols-1 gap-1.5">
                {EMULATOR_DEV_ACCOUNTS.map((a) => (
                  <button
                    key={a.email}
                    onClick={() => {
                      setAuthError('');
                      signInWithEmailAndPassword(auth, a.email, EMULATOR_DEV_PASSWORD).catch(
                        (e: unknown) => {
                          setAuthError(
                            e instanceof Error
                              ? `${a.email}: ${e.message} — is the emulator seeded? (npm run dev:emu)`
                              : 'Emulator sign-in failed.',
                          );
                        },
                      );
                    }}
                    className="flex items-center justify-between w-full bg-white border border-outline-variant text-secondary px-3 py-2 rounded-lg text-sm hover:border-primary/40 active:scale-[0.98] transition-all"
                  >
                    <span className="font-bold uppercase tracking-wider">{a.label}</span>
                    <span className="text-[11px] text-outline normal-case tracking-normal">
                      {a.description}
                    </span>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-outline/80 leading-relaxed mt-2 px-1">
                Seeded accounts from <code>scripts/seed-emulator.mjs</code> — real emulated
                sign-ins, so rules and role claims behave exactly as in prod.
              </p>
            </div>
          )}

          <p className="text-xs text-outline/80 text-center mt-6 px-4 leading-relaxed">
            Access is restricted to authorized company accounts.
          </p>
        </div>
      </main>
    </div>
  );
}
